/**
 * Flexrix (BetConstruct) Partner API — panel tarafının ortak parçaları.
 *
 * Sağlayıcının iki yüzeyi vardır ve panel ikisini birden kullanır: oyun ve
 * para akışı (`/api/flexrix/...`, ayar kartı Oyun Kontrol → Spor & Cashout
 * altında) ile self-servis yönetim (`/partner/v1/*`, bizde
 * `/admin/flexrix/partner/...`). Yollar backend'de sabittir; buradaki
 * sarmalayıcılar yalnızca panelin tekrar tekrar aynı adresi yazmasını önler.
 *
 * Flexrix'in bazı yönetim uçları (`retry-credit`, `key-rotate`, komisyon
 * cüzdanı, maç durumu bildirimi) entegrasyon kartında zaten durur
 * (`routers/flexrix_router.py`); bu dosya onları da aynı çatı altında
 * gösterir, yani yönetim ekranı iki ön eki birden kullanır.
 */
import client from '../../lib/api';

const BASE = '/admin/flexrix';
const PARTNER = `${BASE}/partner`;

/** Sağlayıcı/HTTP hatasının okunabilir metni. */
export const errorText = (error, fallback = 'İstek başarısız') => (
  error?.response?.data?.detail || error?.message || fallback
);

/**
 * Yazma cevabındaki "uygulanmadı" listesi.
 *
 * Sağlayıcı tanımadığı alanı **sessizce** atar; panel bunu yazmazsa yazım
 * hatası olan bir limit kaydedildi sanılır ve kurulum korumasız kalır.
 */
export const ignoredFields = (data) => {
  const rows = data?.ignored_fields;
  return Array.isArray(rows) ? rows.filter(Boolean).map(String) : [];
};

/** JSON metnini nesneye çevirir; hatayı okunur bir cümleye dönüştürür. */
export const parseJson = (text, fallback = {}) => {
  const raw = String(text || '').trim();
  if (!raw) return { value: fallback, error: '' };
  try {
    const value = JSON.parse(raw);
    if (value === null || typeof value !== 'object') {
      return { value: null, error: 'Gövde bir JSON nesnesi olmalıdır' };
    }
    return { value, error: '' };
  } catch (error) {
    return { value: null, error: `Geçersiz JSON: ${error.message}` };
  }
};

/** Nesneyi düzenlenebilir metne çevirir (iki boşluk girinti). */
export const toJsonText = (value) => {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
};

export const partnerApi = {
  // Raporlama ve katalog
  schema: () => client.get(`${PARTNER}/schema`),
  summary: () => client.get(`${PARTNER}/summary`),
  bets: (params) => client.get(`${PARTNER}/bets`, { params }),
  cancelBet: (betId, body) => client.post(
    `${PARTNER}/bets/${encodeURIComponent(betId)}/cancel`, body),
  alerts: (params) => client.get(`${PARTNER}/alerts`, { params }),
  flags: (params) => client.get(`${PARTNER}/flags`, { params }),
  catalog: (sport) => client.get(`${PARTNER}/catalog`, {
    params: sport ? { sport } : undefined,
  }),
  events: (params) => client.get(`${PARTNER}/events`, { params }),
  matchesLive: (limit) => client.get(`${PARTNER}/matches/live`, {
    params: { limit },
  }),
  match: (matchId) => client.get(
    `${PARTNER}/match/${encodeURIComponent(matchId)}`),
  markets: (sport) => client.get(`${PARTNER}/markets`, { params: { sport } }),

  // Ayarlar / görünürlük / risk
  settings: () => client.get(`${PARTNER}/settings`),
  saveSettings: (body) => client.patch(`${PARTNER}/settings`, body),
  visibility: () => client.get(`${PARTNER}/visibility`),
  saveVisibility: (body) => client.patch(`${PARTNER}/visibility`, body),
  riskConfig: () => client.get(`${PARTNER}/risk-config`),
  saveRiskConfig: (body) => client.patch(`${PARTNER}/risk-config`, body),
  riskRules: () => client.get(`${PARTNER}/risk-rules`),
  saveRiskRule: (body) => client.put(`${PARTNER}/risk-rules`, body),
  deleteRiskRule: (id) => client.delete(
    `${PARTNER}/risk-rules`, { params: { id } }),
  riskAudit: () => client.get(`${PARTNER}/risk-audit`),

  // Görünüm
  themePresets: () => client.get(`${PARTNER}/theme-presets`),
  theme: () => client.get(`${PARTNER}/theme`),
  saveTheme: (body) => client.patch(`${PARTNER}/theme`, body),

  // Trading
  tradingLive: (sport) => client.get(`${PARTNER}/trading/live`, {
    params: sport ? { sport } : undefined,
  }),
  tradingMarkets: (fixture) => client.get(`${PARTNER}/trading/markets`, {
    params: { fixture },
  }),
  overrides: (fixture) => client.get(`${PARTNER}/trading/overrides`, {
    params: fixture ? { fixture } : undefined,
  }),
  saveOverride: (body) => client.post(`${PARTNER}/trading/overrides`, body),
  patchOverride: (id, body) => client.patch(
    `${PARTNER}/trading/overrides/${encodeURIComponent(id)}`, body),
  deleteOverride: (id) => client.delete(
    `${PARTNER}/trading/overrides/${encodeURIComponent(id)}`),
  tradingBulk: (body) => client.post(`${PARTNER}/trading/bulk`, body),
  tradingCashout: () => client.get(`${PARTNER}/trading/cashout`),
  saveTradingCashout: (body) => client.post(
    `${PARTNER}/trading/cashout`, body),

  // Markalar
  brands: () => client.get(`${PARTNER}/brands`),
  brand: (id) => client.get(`${PARTNER}/brands/${encodeURIComponent(id)}`),
  replaceBrand: (id, body) => client.put(
    `${PARTNER}/brands/${encodeURIComponent(id)}`, body),
  saveBrand: (id, body) => client.patch(
    `${PARTNER}/brands/${encodeURIComponent(id)}`, body),
  deleteBrand: (id) => client.delete(
    `${PARTNER}/brands/${encodeURIComponent(id)}`),

  // Hesap panelleri
  account: () => client.get(`${PARTNER}/account`),
  saveAccount: (body) => client.patch(`${PARTNER}/account`, body),

  // Günün kombosu & hazır kuponlar
  dailyCombos: () => client.get(`${PARTNER}/daily-combos`),
  createDailyCombo: (body) => client.post(`${PARTNER}/daily-combos`, body),
  saveDailyCombo: (id, body) => client.patch(
    `${PARTNER}/daily-combos/${encodeURIComponent(id)}`, body),
  deleteDailyCombo: (id) => client.delete(
    `${PARTNER}/daily-combos/${encodeURIComponent(id)}`),
  coupons: () => client.get(`${PARTNER}/coupons`),
  createCoupon: (body) => client.post(`${PARTNER}/coupons`, body),
  saveCoupon: (id, body) => client.patch(
    `${PARTNER}/coupons/${encodeURIComponent(id)}`, body),
  deleteCoupon: (id) => client.delete(
    `${PARTNER}/coupons/${encodeURIComponent(id)}`),
  testPlayers: () => client.get(`${PARTNER}/test-players`),
  saveTestPlayers: (body) => client.put(`${PARTNER}/test-players`, body),

  // Banner yönetimi (Hub API)
  banners: () => client.get(`${PARTNER}/banners`),
  createBanner: (body) => client.post(`${PARTNER}/banners`, body),
  saveBanner: (id, body) => client.patch(
    `${PARTNER}/banners/${encodeURIComponent(id)}`, body),
  deleteBanner: (id) => client.delete(
    `${PARTNER}/banners/${encodeURIComponent(id)}`),

  // Takım logoları (Hub API)
  teamLogos: (params) => client.get(`${PARTNER}/team-logos`, { params }),
  uploadTeamLogo: (body) => client.post(`${PARTNER}/team-logos`, body),
  deleteTeamLogo: (id) => client.delete(
    `${PARTNER}/team-logos/${encodeURIComponent(id)}`),

  // Fixtures (Hub API)
  fixtures: (params) => client.get(`${PARTNER}/fixtures`, { params }),
  fixtureMarkets: (matchId, locale) => client.get(
    `${PARTNER}/fixtures/${encodeURIComponent(matchId)}/markets`, {
      params: locale ? { locale } : undefined,
    }),
  featured: (params) => client.get(`${PARTNER}/fixtures/featured`, { params }),
  saveFeatured: (matchId, body) => client.patch(
    `${PARTNER}/fixtures/${encodeURIComponent(matchId)}`, body),

  // Entegrasyon kartında duran uçlar (yönetim ekranıyla aynı çatı).
  retryCredit: (betId) => client.post(
    `${BASE}/credits/${encodeURIComponent(betId)}/retry-credit`),
  rotateKey: () => client.post(`${BASE}/key/rotate`),
  wallet: () => client.get(`${BASE}/wallet`),
};

/** Ödemenin durumu — sağlayıcının kümesi (kupon listesi). */
export const CREDIT_STATUS_LABELS = {
  none: 'Ödeme yok',
  pending: 'Kuyrukta',
  failed: 'Başarısız',
  done: 'Ödendi',
  dead: 'Ölü — elle kuyruğa alın',
};

export const CREDIT_STATUS_TONE = {
  none: 'text-slate-400',
  pending: 'text-sky-300',
  failed: 'text-amber-300',
  done: 'text-emerald-300',
  dead: 'text-rose-400',
};

/** Ödeme yeniden kuyruğa alınabilir mi (yalnızca başarısız/ölü). */
export const canRetryCredit = (status) => (
  status === 'failed' || status === 'dead'
);
